import { BLOCKS } from "@contentful/rich-text-types"
import { graphql } from "gatsby"
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
} from "gatsby-source-contentful/rich-text"
import React, { useMemo } from "react"
import { css } from "styled-components"
import useVideoProgressManager from "../../components/shared/hooks/useVideoProgressManager"
import {
  ContentfulResponsiveImage,
  RESPONSIVE_IMAGE_TYPENAME,
} from "../atoms/breakpoint-image"
import { BUTTON_TYPENAME, ContentfulButton } from "../atoms/button"
import ContentfulAsset from "../atoms/contentful-asset"
import Block from "../atoms/layout-primitives/block"
import Grid from "../atoms/layout-primitives/grid"
import Spacer from "../atoms/layout-primitives/spacer"
import { DefaultRichTextOptions } from "../atoms/rich-text/rich-text"
import { RichText } from "../atoms/text"
import VariantContainer from "../atoms/variant-container"
import Video, { getProgressIndicator, VIDEO_TYPENAME } from "../atoms/video"
import TestimonialCard, {
  TESTIMONIAL_TYPENAME,
} from "../molecules/testimonial-card"
import VideoProgressBar from "../molecules/video-progress-bar"
import { BackgroundColourContextProvider } from "../util/background-colour-context"

export interface IFullWidthContent {
  __typename: "ContentfulFullWidthContent"
  body: RenderRichTextData<ContentfulRichTextGatsbyReference>
  backgroundColour?: { hex: string }
}

const FullWidthContent = ({
  body,
  backgroundColour,
}: Omit<IFullWidthContent, "__typename">) => {
  return (
    <BackgroundColourContextProvider colour={backgroundColour?.hex}>
      <Grid
        variants={[
          css`
            text-align: center;
            justify-items: center;
          `,
        ]}
      >
        <RichText
          options={{
            renderNode: {
              [BLOCKS.HEADING_2]: (node, children) => (
                <Block
                  col={{ sm: "1/-1", md: "2/-2", lg: "3/-3" }}
                  variants={[
                    css`
                      :not(:last-child) {
                        margin-bottom: 24px;
                      }
                    `,
                  ]}
                >
                  {DefaultRichTextOptions.renderNode[BLOCKS.HEADING_2](
                    node,
                    children
                  )}
                </Block>
              ),
              [BLOCKS.PARAGRAPH]: (node, children) => (
                <Block
                  col={{ sm: "1/-1", md: "2/-2", lg: "3/-3" }}
                  variants={[
                    css`
                      :not(:last-child) {
                        margin-bottom: 24px;
                      }
                    `,
                  ]}
                >
                  {DefaultRichTextOptions.renderNode[BLOCKS.PARAGRAPH](
                    node,
                    children
                  )}
                </Block>
              ),
              [BLOCKS.EMBEDDED_ENTRY]: (node) => {
                const component = node?.data?.target

                if (component) {
                  switch (component.__typename) {
                    case BUTTON_TYPENAME:
                      return (
                        <Block
                          display={{ sm: "grid" }}
                          variants={[
                            css`
                              justify-items: center;
                              :not(:first-child) {
                                margin-top: 32px;
                              }
                            `,
                          ]}
                        >
                          <ContentfulButton {...component} />
                        </Block>
                      )
                    case RESPONSIVE_IMAGE_TYPENAME:
                      return (
                        <Block>
                          <ContentfulResponsiveImage {...component} />
                        </Block>
                      )
                    case TESTIMONIAL_TYPENAME:
                      return (
                        <Block
                          variants={[
                            css`
                              background-color: var(--light-background);
                              padding: 24px 16px;
                            `,

                            css`
                              padding: 24px;
                              max-width: 740px;
                            `,
                            css`
                              padding: 32px;
                              max-width: 1118px;
                            `,
                          ]}
                        >
                          <VariantContainer
                            variants={[
                              css`
                                background-color: var(--white);
                                box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.07);
                              `,
                            ]}
                          >
                            <TestimonialCard {...component} />
                          </VariantContainer>
                        </Block>
                      )
                    case VIDEO_TYPENAME:
                      const {
                        videoPlayerRef,
                        videoProgress,
                        setVideoProgress,
                        seekToTimestamp,
                      } = useVideoProgressManager()
                      const { progressDetails, ...rest } = component

                      const progressIndicators = useMemo(
                        () =>
                          progressDetails
                            ? progressDetails.map((item) =>
                                getProgressIndicator(item)
                              )
                            : null,
                        [progressDetails]
                      )
                      return (
                        <>
                          <Block
                            variants={[
                              css`
                                /* push outside bounds of grid on mobile for a full width video */
                                margin-left: -20px;
                                margin-right: -20px;
                                left: 20px;
                                right: 20px;
                                width: 100vw;
                                /* height assuming 16/9 ratio */
                                height: calc(100% * 9 / 16);
                              `,
                              css`
                                /* unset all */
                                margin: unset;
                                left: unset;
                                right: unset;
                                width: 100%;
                                height: unset;
                                grid-column: 2/-2;
                              `,
                            ]}
                          >
                            <Video
                              {...rest}
                              width={"100%"}
                              height={"100%"}
                              onProgress={(data) => setVideoProgress(data)}
                              ref={videoPlayerRef}
                            />
                          </Block>
                          {progressIndicators && (
                            <>
                              <Spacer
                                height={{
                                  sm: "32px",
                                  md: "48px",
                                  lg: "64px",
                                }}
                              />
                              <Block
                                display={{ sm: "grid" }}
                                col={{ sm: "1/-1", md: "2/-2", lg: "3/-3" }}
                                variants={[
                                  css`
                                    gap: 32px;
                                  `,
                                  css`
                                    grid-template-columns: 1fr 1fr;
                                  `,
                                ]}
                              >
                                {progressIndicators.map((details, index) => (
                                  <VideoProgressBar
                                    key={index}
                                    details={details}
                                    videoProgress={videoProgress}
                                    seekMethod={seekToTimestamp}
                                    titleVariants={[
                                      css`
                                        color: var(--slate);
                                      `,
                                    ]}
                                  />
                                ))}
                              </Block>
                            </>
                          )}
                        </>
                      )
                    default:
                      return null
                  }
                }
              },
              [BLOCKS.EMBEDDED_ASSET]: (node) => {
                const asset = node?.data?.target
                return asset ? (
                  <Block>
                    <ContentfulAsset
                      {...asset}
                      variants={[
                        css`
                          margin: 0 auto;
                        `,
                      ]}
                    />
                  </Block>
                ) : null
              },
            },
          }}
        >
          {body}
        </RichText>
      </Grid>
    </BackgroundColourContextProvider>
  )
}

export const FullWidthContentFragment = graphql`
  fragment ContentfulFullWidthContent on ContentfulFullWidthContent {
    contentful_id
    body {
      raw
      references {
        __typename
        ... on ContentfulButton {
          ...ContentfulButton
        }
        ... on ContentfulVideo {
          ...ContentfulVideo
          progressDetails {
            key
            value
          }
        }
        ... on ContentfulResponsiveImage {
          ...ContentfulResponsiveImage
        }
        ... on ContentfulAsset {
          ...ContentfulAsset
        }
        ... on ContentfulTestimonial {
          ...ContentfulTestimonial
        }
      }
    }
    backgroundColour {
      hex
    }
  }
`
export const FULL_WIDTH_CONTENT_TYPENAME = "ContentfulFullWidthContent"
export default FullWidthContent
